import React, { useState } from "react";
import { useRequest } from "../hooks";
import { ALERT_INITIAL_STATE } from "../utils/data";
import SuperAdminContext, {
  FlushPreview,
  SuperAdminTransactionRow,
  SuperAdminUserPayload,
} from "./SuperAdminContext";
import { ClientProps, UserProps } from "../interfaces/interfaces";

interface Props {
  children: JSX.Element | JSX.Element[];
}

const buildQuery = (filters?: { clientId?: string; search?: string }) => {
  const params = new URLSearchParams();
  if (filters?.clientId) params.set("clientId", filters.clientId);
  if (filters?.search) params.set("search", filters.search);
  const query = params.toString();
  return query ? `?${query}` : "";
};

const SuperAdminProvider = ({ children }: Props) => {
  const { handleRequest } = useRequest();
  const [companies, setCompanies] = useState<ClientProps[]>([]);
  const [users, setUsers] = useState<UserProps[]>([]);
  const [transactionRows, setTransactionRows] = useState<
    SuperAdminTransactionRow[]
  >([]);
  const [flushPreview, setFlushPreview] = useState<FlushPreview | null>(null);
  const [superAdminLoading, setSuperAdminLoading] = useState(false);
  const [superAdminError, setSuperAdminError] = useState(ALERT_INITIAL_STATE);
  const [superAdminSuccess, setSuperAdminSuccess] =
    useState(ALERT_INITIAL_STATE);

  const request = ({
    endpoint,
    options,
    onSuccess,
    successMessage,
  }: {
    endpoint: string;
    options?: RequestInit;
    onSuccess?: (data: any) => void;
    successMessage?: string;
  }) => {
    setSuperAdminLoading(true);
    setSuperAdminError(ALERT_INITIAL_STATE);
    setSuperAdminSuccess(ALERT_INITIAL_STATE);
    handleRequest({
      endpoint,
      options,
      onSuccess: (response) => {
        onSuccess && onSuccess(response.data);
        successMessage &&
          setSuperAdminSuccess({ status: true, message: successMessage });
        setSuperAdminLoading(false);
      },
      onError: (e) => {
        setSuperAdminError({
          status: true,
          message: e?.message || "Please try again",
        });
        setSuperAdminLoading(false);
      },
    });
  };

  const getCompanies = () => {
    request({
      endpoint: "super-admin/companies",
      onSuccess: setCompanies,
    });
  };

  const getUsers = (filters?: { clientId?: string; search?: string }) => {
    request({
      endpoint: `super-admin/users${buildQuery(filters)}`,
      onSuccess: setUsers,
    });
  };

  const createUser = (data: SuperAdminUserPayload, next?: () => void) => {
    request({
      endpoint: "super-admin/users",
      options: {
        method: "POST",
        body: JSON.stringify(data),
      },
      successMessage: "User saved",
      onSuccess: () => next && next(),
    });
  };

  const updateUser = (
    idUser: string,
    data: SuperAdminUserPayload,
    next?: () => void,
  ) => {
    request({
      endpoint: `super-admin/users/${idUser}`,
      options: {
        method: "PUT",
        body: JSON.stringify(data),
      },
      successMessage: "User updated",
      onSuccess: () => next && next(),
    });
  };

  const removeUserFromCompany = (
    idUser: string,
    clientId: string,
    next?: () => void,
  ) => {
    request({
      endpoint: `super-admin/users/${idUser}?clientId=${clientId}`,
      options: {
        method: "DELETE",
      },
      successMessage: "User removed from company",
      onSuccess: () => next && next(),
    });
  };

  const getTransactionCounts = () => {
    request({
      endpoint: "super-admin/transactions",
      onSuccess: setTransactionRows,
    });
  };

  const getFlushPreview = (idClient: string) => {
    request({
      endpoint: `super-admin/flush/${idClient}/preview`,
      onSuccess: setFlushPreview,
    });
  };

  const flushCompany = (
    idClient: string,
    confirmCompanyName: string,
    next?: (deletedCounts: FlushPreview["counts"]) => void,
  ) => {
    request({
      endpoint: `super-admin/flush/${idClient}`,
      options: {
        method: "POST",
        body: JSON.stringify({ confirmCompanyName }),
      },
      successMessage: "Database flush complete",
      onSuccess: (data) => next && next(data),
    });
  };

  const updateCompanySubscription = (
    idClient: string,
    active: boolean,
    next?: () => void,
  ) => {
    request({
      endpoint: `super-admin/companies/${idClient}/subscription`,
      options: {
        method: "PATCH",
        body: JSON.stringify({ active }),
      },
      successMessage: active ? "Company activated" : "Company suspended",
      onSuccess: () => next && next(),
    });
  };

  return (
    <SuperAdminContext.Provider
      value={{
        companies,
        users,
        transactionRows,
        flushPreview,
        superAdminLoading,
        superAdminError,
        superAdminSuccess,
        getCompanies,
        getUsers,
        createUser,
        updateUser,
        removeUserFromCompany,
        getTransactionCounts,
        getFlushPreview,
        flushCompany,
        updateCompanySubscription,
      }}
    >
      {children}
    </SuperAdminContext.Provider>
  );
};

export default SuperAdminProvider;
